import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiSend, FiClock, FiAlertTriangle, FiBell, FiDownload, FiUsers, FiUpload } from "react-icons/fi";
import {
  getPendingTasks, sendReminders, getReminderRules, updateReminderRule, getReminderLog, dispatchReminders,
} from "../../services/reminderApi";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import useAsync from "../../hooks/useAsync";
import {
  Card, PageHeader, Button, DataTable, SearchInput, Banner, Modal, FormField,
  Tabs, StatTile, Loader, EmptyState,
} from "../../components/common";
import { PersonCell } from "../../components/common/Avatar";
import { REMINDER_FREQUENCIES, REMINDER_TASK_TYPES } from "../../utils/constants";
import { formatDate, formatDateTime, timeAgo } from "../../utils/format";
import { downloadXlsx } from "../../utils/exportExcel";
import { getErrorMessage } from "../../utils/errors";
import { VIEWS } from "../../services/navigation";
import { can, ORG_SCOPES } from "../../services/core";
import Select from "../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared organisation filters
import OrgFilters from "../../components/common/OrgFilters";
import { lastAutoReminderRun } from "../../hooks/useAutoReminders"; // [NEW] PM-10
import MultiSelect from "../../components/common/MultiSelect"; // [NEW] PM-18
import RowImportModal from "../../components/imports/RowImportModal"; // [NEW] import reminder rules

// [NEW] "Import rules": one row per stage. Each row replaces that stage's rule (or switches it off).
const RULE_STAGE_NAMES = { GOAL_SETTING: "Goal setting", GOAL_APPROVAL: "Goal approval", SELF_ASSESSMENT: "Self assessment", MANAGER_ASSESSMENT: "Manager review", HR_FINALIZATION: "HR finalisation" };
const RULE_IMPORT_COLUMNS = [
  { key: "stage", label: "Stage", required: true, example: "Goal setting", hint: Object.values(RULE_STAGE_NAMES).join(", ") },
  { key: "enabled", label: "Active", required: true, example: "Yes", hint: "Yes or No" },
  { key: "daysBeforeDue", label: "Days before due", example: "3", hint: "0 to 30 (default 3)" },
  { key: "frequency", label: "Frequency", example: "Every 2 days", hint: "Daily, Every 2 days, Weekly, Once or Every N days" },
  { key: "escalationEmployeeId", label: "Escalate to (employee ID)", example: "", hint: "optional" },
  { key: "escalateAfterDays", label: "Escalate after days", example: "", hint: "optional, needed with Escalate to" },
];
const norm = (t) => String(t || "").toLowerCase().replace(/[^a-z]/g, "");
function checkRuleRow(row, i, all) {
  const errors = [];
  const stage = Object.keys(RULE_STAGE_NAMES).find((k) => norm(k) === norm(row.stage) || norm(RULE_STAGE_NAMES[k]) === norm(row.stage));
  if (!stage) errors.push(`Stage must be one of: ${Object.values(RULE_STAGE_NAMES).join(", ")}.`);
  else if (all.slice(0, i).some((r) => norm(r.stage) === norm(row.stage))) errors.push("This stage is already in an earlier row.");
  const yes = ["yes", "y", "true", "1", "active", "on"].includes(String(row.enabled).trim().toLowerCase());
  const no = ["no", "n", "false", "0", "inactive", "off"].includes(String(row.enabled).trim().toLowerCase());
  if (!yes && !no) errors.push("Active must be Yes or No.");
  const days = row.daysBeforeDue === "" ? 3 : Number(row.daysBeforeDue);
  if (!Number.isInteger(days) || days < 0 || days > 30) errors.push("Days before due must be a whole number 0 to 30.");
  const frequency = row.frequency || "Every 2 days";
  if (!REMINDER_FREQUENCIES.includes(frequency) && !/^Every \d+ days$/.test(frequency)) errors.push("Frequency must be Daily, Every 2 days, Weekly, Once or Every N days.");
  if (Boolean(row.escalationEmployeeId) !== Boolean(row.escalateAfterDays)) errors.push("Give both Escalate to and Escalate after days, or neither.");
  if (errors.length) return { value: null, errors };
  return { errors: [], value: { stage, enabled: yes, daysBeforeDue: days, frequency, escalationEmployeeId: row.escalationEmployeeId || null, escalateAfterDays: row.escalateAfterDays ? Number(row.escalateAfterDays) : null } };
}

const api = { getPendingTasks, sendReminders, getReminderRules, updateReminderRule, getReminderLog, dispatchReminders };

const DEFAULT_MESSAGE = "This is a reminder that the task below is still pending. Please complete it before the due date so the cycle stays on track.";

function OverdueCell({ row }) {
  if (row.daysOverdue > 0) {
    return <span className="badge badge-danger"><span className="badge-dot" />{row.daysOverdue} d overdue</span>;
  }
  const days = Math.ceil((new Date(row.dueDate) - new Date()) / 86400000);
  if (days <= 3) return <span className="badge badge-warning"><span className="badge-dot" />Due in {Math.max(0, days)} d</span>;
  return <span className="badge badge-neutral"><span className="badge-dot" />{formatDate(row.dueDate)}</span>;
}

export default function RemindersPage({ scope = "org" }) {
  const { cycleId, cycle } = useCycle();
  const { activeView, profile } = useRole();
  const canSend = can(profile, "reminder.manage", ORG_SCOPES);
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [taskType, setTaskType] = useState([]); // [UPDATED] PM-18: several task types at once
  const org = useOrgFilters(); // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity
  const [selected, setSelected] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [busy, setBusy] = useState(false);

  const effectiveScope = activeView === VIEWS.MANAGER ? "team" : scope;
  const pending = useAsync(() => api.getPendingTasks(cycleId, { scope: effectiveScope }), [cycleId, effectiveScope], { skip: !cycleId });
  const [importingRules, setImportingRules] = useState(false); // [NEW]
  const rules = useAsync(() => api.getReminderRules(cycleId), [cycleId], { skip: !cycleId || effectiveScope === "team" || !canSend });
  const log = useAsync(() => api.getReminderLog({ cycleId }), [cycleId]);
  const [dispatching, setDispatching] = useState(false);

  const dispatchNow = async () => {
    setDispatching(true);
    try {
      const { data } = await api.dispatchReminders();
      toast.success(`Automatic rules ran: ${data.matched ?? 0} reminder${data.matched === 1 ? "" : "s"} sent.`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const tasks = useMemo(() => pending.data || [], [pending.data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter(
      (t) =>
        (!taskType.length || taskType.includes(t.taskType)) &&
        org.matches({ id: t.employeeId }) &&
        (!q || t.employeeName.toLowerCase().includes(q) || t.departmentName.toLowerCase().includes(q))
    );
  }, [tasks, search, taskType, org.matches]); // eslint-disable-line react-hooks/exhaustive-deps

  const overdue = tasks.filter((t) => t.daysOverdue > 0);
  const neverReminded = tasks.filter((t) => !t.lastRemindedAt);
  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.id));

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(allSelected ? [] : rows.map((r) => r.id));

  const openCompose = (ids) => {
    if (!ids.length) { toast.error("Pick at least one pending task."); return; }
    setSelected(ids);
    setMessage(DEFAULT_MESSAGE);
    setComposeOpen(true);
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.sendReminders({ taskIds: selected, message, tasks: tasks.filter((t) => selected.includes(t.id)) });
      toast.success(`Reminder sent for ${data.sent} ${data.sent === 1 ? "task" : "tasks"}${data.skipped ? ` (${data.skipped} skipped)` : ""}.`);
      setComposeOpen(false);
      setSelected([]);
      pending.reload();
      log.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleRule = async (rule, patch) => {
    try {
      await api.updateReminderRule(rule.id, patch, rule);
      rules.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const exportPending = () => {
    downloadXlsx(`pending-tasks-${cycle?.name || ""}`, [
      {
        name: "Pending tasks",
        title: `Pending tasks — ${cycle?.name || ""}`,
        columns: [
          { key: "employeeName", label: "Employee" },
          { key: "designationName", label: "Designation" },
          { key: "departmentName", label: "Department" },
          { key: "managerName", label: "Manager" },
          { key: "taskType", label: "Pending task" },
          { key: "detail", label: "Detail" },
          { key: "dueDate", label: "Due date", value: (r) => formatDate(r.dueDate) },
          { key: "daysOverdue", label: "Days overdue" },
          { key: "reminderCount", label: "Reminders sent" },
          { key: "lastRemindedAt", label: "Last reminded", value: (r) => (r.lastRemindedAt ? formatDateTime(r.lastRemindedAt) : "Never") },
        ],
        rows,
      },
    ]);
    toast.success("Pending tasks exported to Excel.");
  };

  return (
    <div className="page">
      <PageHeader
        title="Reminders"
        subtitle={`Pending goals, assessments and tasks in ${cycle?.name}. Nudge people manually or let the rules do it.`}
        actions={
          <>
            <Button variant="secondary" icon={FiDownload} onClick={exportPending} disabled={!rows.length}>Export Excel</Button>
            {canSend && (
              <Button icon={FiSend} onClick={() => openCompose(selected)} disabled={!selected.length}>
                Send reminder{selected.length ? ` (${selected.length})` : ""}
              </Button>
            )}
          </>
        }
      />

      {/* [UPDATED] PM-11: sending reminders is a permission (reminder.manage) given per role under Admin › Users & roles. */}
      {!canSend && (
        <Banner tone="info" title={effectiveScope === "team" ? "HR sends the reminders" : "You can't send reminders yet"}>
          {effectiveScope === "team"
            ? "You can see who still owes something. Sending reminders and automatic rules are managed by HR."
            : "Your role does not have the Send reminders permission (reminder.manage). An administrator can give it to your role under Admin › Users & roles › Role permissions."}
        </Banner>
      )}
      {canSend && effectiveScope !== "team" && (
        <Banner tone="neutral" title="Automatic reminders">
          {/* [NEW] PM-10 */}
          The active rules below run automatically once a day (last run {lastAutoReminderRun() ? formatDateTime(lastAutoReminderRun()) : "not yet on this computer"}).
          Employees who have not set or submitted their goals are reminded by the Goal setting rule until the deadline.
        </Banner>
      )}

      <div className="stat-grid">
        <StatTile label="Pending tasks" value={tasks.length} icon={FiClock} hint={effectiveScope === "team" ? "Across your team" : "Across the organisation"} />
        <StatTile label="Overdue" value={overdue.length} icon={FiAlertTriangle} tone="danger" hint="Past the stage deadline" />
        <StatTile label="Never reminded" value={neverReminded.length} icon={FiBell} tone="warning" hint="No nudge sent yet" />
        <StatTile label="Active rules" value={(rules.data || []).filter((r) => r.enabled).length} icon={FiUsers} tone="success" hint="Automatic reminders" />
      </div>

      {overdue.length > 0 && (
        <Banner
          tone="warning"
          title={`${overdue.length} ${overdue.length === 1 ? "task is" : "tasks are"} overdue`}
          action={canSend && <Button size="sm" icon={FiSend} onClick={() => openCompose(overdue.map((t) => t.id))}>Remind all overdue</Button>}
        >
          Sending a reminder writes an in-app notification for the person who owns the pending step.
        </Banner>
      )}

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "pending", label: "Pending tasks", count: tasks.length },
          { key: "rules", label: "Automatic rules", count: (rules.data || []).filter((r) => r.enabled).length },
          { key: "log", label: "Reminder log", count: (log.data || []).length },
        ].filter((t) => canSend || t.key === "pending")}
      />

      {tab === "pending" && (
        <Card
          padded={false}
          title="Who still owes something"
          actions={
            <div className="filter-bar">
              <MultiSelect label="Task type" allLabel="All task types" options={Object.values(REMINDER_TASK_TYPES).map((t) => ({ value: t, label: t }))} value={taskType} onChange={setTaskType} />
              <OrgFilters org={org} />
              <SearchInput value={search} onChange={setSearch} placeholder="Find a person" />
            </div>
          }
        >
          {pending.loading && !pending.data ? (
            <Loader />
          ) : (
            <DataTable
              rows={rows} sortBy="employeeName"
              pageSize={10}
              emptyTitle="Nothing pending"
              emptyText="Every goal and assessment in this cycle is up to date."
              columns={[
                {
                  key: "pick",
                  label: "",
                  width: "44px",
                  render: (r) => (
                    <label className="checkbox">
                      <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} aria-label={`Select ${r.employeeName}`} />
                    </label>
                  ),
                },
                {
                  key: "employeeName",
                  label: (
                    <button type="button" className="link-btn" onClick={toggleAll}>
                      {allSelected ? "Clear all" : "Select all"}
                    </button>
                  ),
                  render: (r) => <PersonCell name={r.employeeName} meta={`${r.designationName}, ${r.departmentName}`} />,
                },
                { key: "taskType", label: "Pending", render: (r) => <span className="cell-stack"><strong>{r.taskType}</strong><small>{r.detail}</small></span> },
                { key: "dueDate", label: "Due", render: (r) => <OverdueCell row={r} /> },
                {
                  key: "lastRemindedAt",
                  label: "Last reminder",
                  render: (r) => (
                    <span className="cell-stack">
                      <span>{r.lastRemindedAt ? timeAgo(r.lastRemindedAt) : "Never"}</span>
                      <small>{r.reminderCount || 0} sent</small>
                    </span>
                  ),
                },
                {
                  key: "act",
                  label: "",
                  align: "right",
                  render: (r) => canSend && <Button variant="secondary" size="sm" icon={FiSend} onClick={() => openCompose([r.id])}>Remind</Button>,
                },
              ]}
            />
          )}
        </Card>
      )}

      {tab === "rules" && (
        <Card
          title="Automatic reminders"
          subtitle="One rule per stage for this cycle. A scheduled worker (python -m app.worker) sends them daily; notifications are in-app, plus e-mail when SMTP is set up on the server."
          actions={(
            <>
              <Button size="sm" variant="ghost" icon={FiUpload} onClick={() => setImportingRules(true)}>Import rules</Button>
              <Button size="sm" variant="secondary" icon={FiSend} loading={dispatching} onClick={dispatchNow}>Run rules now</Button>
            </>
          )}
        >
          {rules.loading && !rules.data ? <Loader /> : (
            <ul className="rule-list">
              {(rules.data || []).map((rule) => (
                <li key={rule.id} className={`rule-row ${rule.enabled ? "" : "is-off"}`}>
                  <div className="rule-main">
                    <label className="switch">
                      <input type="checkbox" checked={rule.enabled} onChange={() => toggleRule(rule, { enabled: !rule.enabled })} />
                      <span className="switch-track" aria-hidden="true" />
                      <span className="switch-label">{rule.taskType}</span>
                    </label>
                    <p className="muted">
                      {rule.enabled
                        ? `${rule.frequency}, starting ${rule.daysBeforeDue} days before the due date, via ${rule.channels.join(" and ")}.`
                        : "Off. Nobody is reminded automatically for this."}
                      {rule.enabled && rule.escalateToManager && ` Escalates to the reporting manager after ${rule.escalateAfterDays} days.`}
                    </p>
                  </div>
                  <div className="rule-controls">
                    <label className="select-inline">
                      <span className="sr-only">{rule.taskType} frequency</span>
                      <Select className="form-control" value={rule.frequency} disabled={!rule.enabled} onChange={(e) => toggleRule(rule, { frequency: e.target.value })}>
                        {[...new Set([...REMINDER_FREQUENCIES, rule.frequency])].map((f) => <option key={f} value={f}>{f}</option>)}
                      </Select>
                    </label>
                    <label className="select-inline">
                      <span className="sr-only">{rule.taskType} days before the deadline</span>
                      <Select className="form-control" value={rule.daysBeforeDue} disabled={!rule.enabled} onChange={(e) => toggleRule(rule, { daysBeforeDue: Number(e.target.value) })}>
                        {[...new Set([0, 1, 2, 3, 5, 7, 14, Number(rule.daysBeforeDue)])].sort((a, b) => a - b).map((d) => <option key={d} value={d}>{d} days before</option>)}
                      </Select>
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {tab === "log" && (
        <Card padded={false} title="Reminders already sent">
          {log.loading && !log.data ? <Loader /> : (log.data || []).length === 0 ? (
            <EmptyState title="No reminders yet" text="Nudges you send will be listed here." />
          ) : (
            <DataTable keepOrder
              rows={log.data}
              pageSize={10}
              columns={[
                { key: "sentAt", label: "Sent", render: (r) => <span className="cell-stack"><span>{formatDateTime(r.sentAt)}</span><small>{r.sentBy}</small></span> },
                { key: "taskType", label: "For" },
                { key: "recipients", label: "Recipients", render: (r) => r.recipients.join(", ") },
                { key: "channels", label: "Channels", render: (r) => r.channels.join(", ") },
                { key: "message", label: "Message", render: (r) => <span className="muted">{r.message}</span> },
              ]}
            />
          )}
        </Card>
      )}

      <Modal
        open={composeOpen}
        size="md"
        title={`Send a reminder to ${selected.length} ${selected.length === 1 ? "person" : "people"}`}
        subtitle="Each person gets an in-app reminder for their pending task."
        onClose={busy ? undefined : () => setComposeOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setComposeOpen(false)} disabled={busy}>Cancel</Button>
            <Button icon={FiSend} onClick={send} loading={busy}>Send reminder</Button>
          </>
        }
      >
        <div className="form-stack">
          <div className="reminder-recipients">
            {tasks.filter((t) => selected.includes(t.id)).map((t) => (
              <span key={t.id} className="tag tag-info">{t.employeeName} · {t.taskType}</span>
            ))}
          </div>
          <Banner tone="info" title="What the person receives">
            An in-app reminder with the pending task and its due date. The note below is saved with the
            reminder in the audit log; the current backend doesn&apos;t include your own text in the message yet.
          </Banner>
          <FormField label="Note (kept in the audit log)" hint="Why you are sending this reminder.">
            <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
          </FormField>
        </div>
      </Modal>
      <RowImportModal
        open={importingRules}
        title="Import reminder rules"
        subtitle={`One row per stage for ${cycle?.name || "this cycle"}. Each row replaces that stage's current rule.`}
        templateName="reminder-rules-template"
        columns={RULE_IMPORT_COLUMNS}
        validate={checkRuleRow}
        importRow={(value) => {
          const current = (rules.data || []).find((r) => r.stage === value.stage) || { cycleId: Number(cycle?.cycleId || cycleId), stage: value.stage };
          return api.updateReminderRule(current.ruleId, value, current);
        }}
        onClose={() => setImportingRules(false)}
        onDone={() => rules.reload()}
      />
    </div>
  );
}
